import React, { useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import { Modal } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLayerGroup } from "@fortawesome/free-solid-svg-icons";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { updateDutyRequestStatusMutation } from "@src/util/mutation/dutyRequest.mutation";
import { getSiteQuery } from "@src/util/query/site.query";
import { NewMobileResponseModal } from "@src/pages/mobileResponseServices/newMobileResponse.modal";
import { AddDutyModal } from "@src/pages/schedule/addDuty/addDuty.model";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";

/**
 * What can be done with a customer's service request - push it to the schedule (Add Duty,
 * asking for the site area first when the site has more than one) or to mobile services (New
 * Mobile Response), both prefilled from the request and marking it pushed once saved, or
 * reject it with a reason. Shared by the Service Request page and the dashboard tile.
 * Render `modals` once; `onChanged` runs after the request's status changes.
 */
export const useServiceRequestActions = ({ onChanged } = {}) => {
    const dispatch = useDispatch();
    const [request, setRequest] = useState(null);
    const [mobileModal, setMobileModal] = useState(false);
    const [scheduleModal, setScheduleModal] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [pushedSite, setPushedSite] = useState(null);
    const [siteLoading, setSiteLoading] = useState(false);
    const [areaPickerModal, setAreaPickerModal] = useState(false);
    const [selectedAreaId, setSelectedAreaId] = useState(null);

    const [fetchSite] = useLazyQuery(getSiteQuery, { fetchPolicy: "network-only" });
    const [updateStatus] = useMutation(updateDutyRequestStatusMutation);

    const pushToSchedule = async (row) => {
        setRequest(row);
        setSiteLoading(true);
        try {
            const { data } = await fetchSite({ variables: { id: row.siteId } });
            const site = data?.site?.response || null;
            setPushedSite(site);
            const areas = site?.siteArea || [];
            setSelectedAreaId(areas.length === 1 ? areas[0]._id : null);
            if (areas.length > 1) {
                setAreaPickerModal(true);
            } else {
                setScheduleModal(true);
            }
        } catch {
            setPushedSite(null);
            setScheduleModal(true);
        } finally {
            setSiteLoading(false);
        }
    };

    const pushToMobile = (row) => {
        setRequest(row);
        setMobileModal(true);
    };

    const confirmAreaAndOpenSchedule = () => {
        setAreaPickerModal(false);
        setScheduleModal(true);
    };

    const reject = async (row) => {
        const res = await Swal.fire({
            title: "Reject this request?",
            input: "textarea",
            inputLabel: "Reason for rejection",
            inputPlaceholder: "Explain why this request is being rejected...",
            inputAttributes: { rows: 3, style: "font-size:14px;resize:none;" },
            inputValidator: (v) => !v?.trim() ? "Please provide a rejection reason" : null,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#6c757d",
            confirmButtonText: "Reject",
            cancelButtonText: "Cancel",
        });
        if (!res.isConfirmed) return;

        setActionLoading(true);
        try {
            await updateStatus({ variables: { requestId: row._id, status: "rejected", rejectionReason: res.value } });
            dispatch(setNotification({ error: false, notificationState: true, message: "Request rejected" }));
            onChanged?.();
        } catch {
            dispatch(setNotification({ error: true, notificationState: true, message: "Failed to update status" }));
        } finally {
            setActionLoading(false);
        }
    };

    const markPushed = async (requestId) => {
        setActionLoading(true);
        try {
            await updateStatus({ variables: { requestId, status: "pushed" } });
            dispatch(setNotification({ error: false, notificationState: true, message: "Request marked as pushed to schedule" }));
            setMobileModal(false);
            setScheduleModal(false);
            onChanged?.();
        } catch {
            dispatch(setNotification({ error: true, notificationState: true, message: "Failed to update status" }));
        } finally {
            setActionLoading(false);
        }
    };

    const modals = (
        <>
            {/* ── Mobile Services Modal ── */}
            {mobileModal && request && (
                <NewMobileResponseModal
                    visible={mobileModal}
                    close={() => setMobileModal(false)}
                    refetch={async () => {
                        await markPushed(request._id);
                    }}
                    prefillSiteId={request.siteId}
                    prefillPriority={request.priority}
                    prefillDutyTypeId={request.dutyTypeId}
                    prefillStartTime={request.preferredStartTime}
                    prefillEndTime={request.preferredEndTime}
                    prefillNotes={request.notes}
                    prefillScheduleType={request.scheduleType}
                    prefillScheduleDays={request.scheduleDays}
                    prefillFrequencyDays={request.frequencyDays?.toString()}
                />
            )}

            {/* ── Area Picker Modal ── */}
            {areaPickerModal && pushedSite && (
                <Modal
                    open={areaPickerModal}
                    closable={false}
                    width={Math.min(440, typeof window !== 'undefined' ? window.innerWidth - 32 : 440)}
                    zIndex={1100}
                    footer={
                        <div className="drawer-footer">
                            <button
                                className="drawer-filled-btn"
                                disabled={!selectedAreaId}
                                onClick={confirmAreaAndOpenSchedule}
                            >
                                Continue to Schedule
                            </button>
                            <button
                                className="drawer-outlined-btn"
                                onClick={() => { setAreaPickerModal(false); setPushedSite(null); }}
                            >
                                Cancel
                            </button>
                        </div>
                    }
                >
                    <div className="modal-header-title">
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                            <FontAwesomeIcon icon={faLayerGroup} style={{ color: "#126e7d", fontSize: 16 }} />
                            <span>Select Site Area</span>
                        </div>
                    </div>
                    <div className="modal-body">
                        <div style={{ fontSize: 13, color: "#7c98b6", marginBottom: 14 }}>
                            Choose which area of <strong style={{ color: "#33475b" }}>{pushedSite.sitename}</strong> this duty is for:
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {(pushedSite.siteArea || []).map(area => (
                                <div
                                    key={area._id}
                                    onClick={() => setSelectedAreaId(area._id)}
                                    style={{
                                        padding: "12px 16px",
                                        borderRadius: 6,
                                        border: `2px solid ${selectedAreaId === area._id ? "#126e7d" : "#dfe3eb"}`,
                                        background: selectedAreaId === area._id ? "#e5f5f8" : "#fff",
                                        cursor: "pointer",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 10,
                                        transition: "all 0.15s",
                                    }}
                                >
                                    <div style={{
                                        width: 10, height: 10, borderRadius: "50%",
                                        background: selectedAreaId === area._id ? "#126e7d" : "#cbd6e2",
                                        flexShrink: 0,
                                    }} />
                                    <span style={{
                                        fontWeight: selectedAreaId === area._id ? 600 : 500,
                                        color: selectedAreaId === area._id ? "#126e7d" : "#33475b",
                                        fontSize: 14,
                                    }}>
                                        {area.areaname}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </Modal>
            )}

            {/* ── Schedule Modal ── */}
            {scheduleModal && request && (
                <AddDutyModal
                    dutyModal={{
                        visible: true,
                        title: (() => {
                            const dateStr = request.preferredDate
                                || request.preferredStartTime?.split(" ")?.[0];
                            return dateStr ? dayjs(dateStr, "DD/MM/YYYY") : dayjs();
                        })(),
                        site: pushedSite,
                        area: (pushedSite?.siteArea || []).find(a => a._id?.toString() === selectedAreaId?.toString()) || null,
                    }}
                    prefillShiftStart={request.preferredStartTime?.split(" ")?.[1] || ""}
                    prefillShiftEnd={request.preferredEndTime?.split(" ")?.[1] || ""}
                    prefillDutyTypeId={request.dutyTypeId || ""}
                    prefillNumberOfDuties={request.numberOfDuties || null}
                    close={() => { setScheduleModal(false); setPushedSite(null); setSelectedAreaId(null); }}
                    scheduleRefetch={async () => {
                        await markPushed(request._id);
                    }}
                />
            )}
        </>
    );

    return { pushToSchedule, pushToMobile, reject, siteLoading, actionLoading, modals };
};
